import { z } from 'zod';

/**
 * SIL-924 — per-source freshness DTO, powering GET
 * /api/market-comp-sources/freshness.
 *
 * Reads market_comp_source_freshness (SIL-922, db/schema/
 * 822_market_comp_source_freshness.sql), the table the ingestion
 * orchestrator's computeSourceCompAges() upserts into on every run — one row
 * per market_comp_source. This is the shape SIL-925's ops UI panel consumes.
 *
 * Field semantics mirror the table's own column comments verbatim:
 *   - newestObservedAt / ageDays are both null exactly when the source has
 *     landed zero market_comp rows.
 *   - isStale is a SNAPSHOT taken at the orchestrator run named by
 *     computedAt, evaluated against staleAfterDays (SIL-921) at that
 *     moment — not re-evaluated live on read. A source with a never-run/
 *     never-scraped state (ageDays null) is treated as stale.
 *   - computedAt far in the past signals a stalled/broken orchestrator run
 *     (the whole table goes visibly stale), not just a stale source.
 */
export const marketCompSourceFreshnessRowSchema = z.object({
  sourceId: z.string(),
  sourceName: z.string(),
  newestObservedAt: z.string().nullable(),
  ageDays: z.number().nullable(),
  staleAfterDays: z.number(),
  isStale: z.boolean(),
  computedAt: z.string(),
});
export type MarketCompSourceFreshnessRow = z.infer<typeof marketCompSourceFreshnessRowSchema>;

/** Response for GET /api/market-comp-sources/freshness */
export const marketCompSourceFreshnessResponseSchema = z.object({
  data: z.array(marketCompSourceFreshnessRowSchema),
});
export type MarketCompSourceFreshnessResponse = z.infer<
  typeof marketCompSourceFreshnessResponseSchema
>;
